import React, { useState, useEffect } from 'react';
import { BookOpen, Heart, MessageSquare, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { BibleVerse } from '../lib/types';
import { useFavoritesStore } from '../stores/favoritesStore';
import { useNotesStore } from '../stores/notesStore';
import { NoteModal } from './NoteModal';
import { useAuthStore } from '../services/auth';

interface VerseCardProps {
  verse: BibleVerse;
  showContext?: boolean;
}

export function VerseCard({ verse, showContext }: VerseCardProps) {
  const navigate = useNavigate();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);
  const toggleFavorite = useFavoritesStore((state) => state.toggleFavorite);
  const favorites = useFavoritesStore((state) => state.favorites);
  const loadFavorites = useFavoritesStore((state) => state.loadFavorites);
  const hasVerseNotes = useNotesStore((state) => state.hasNotes);
  const [isFavorited, setIsFavorited] = useState(false);
  const { user, setShowAuthModal } = useAuthStore();

  // Load favorites on mount if not already loaded
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Update favorite status when favorites change
  useEffect(() => {
    setIsFavorited(!!favorites[verse.id]);
  }, [favorites, verse.id]);

  useEffect(() => {
    const checkNotes = async () => {
      // Format verse_id to match the format in the database
      const verseId = `${verse.book.toLowerCase()}_${verse.chapter}_${verse.verse}`;
      const hasVerseNote = await hasVerseNotes(verseId);
      setHasNotes(hasVerseNote);
    };
    checkNotes();
  }, [verse, hasVerseNotes]);

  const handleReadInContext = () => {
    const bookForUrl = verse.book.toLowerCase().replace(/ /g, '-');
    navigate(`/read/${verse.version}/${bookForUrl}/${verse.chapter}`);
  };

  const handleFavoriteClick = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    await toggleFavorite({
      id: verse.id,
      book: verse.book,
      chapter: verse.chapter,
      verse: verse.verse,
      text: verse.text,
      version: verse.version
    });
  };

  const handleNoteClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setIsNoteModalOpen(true);
  };

  return (
    <div className="p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all group">
      <div className="flex justify-between items-start gap-4 mb-2">
        <h3 className="text-gold-400 font-medium flex items-center gap-2">
          {verse.reference}
          <span className="text-sm text-white/40">({verse.version.toUpperCase()})</span>
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleFavoriteClick}
            className={`p-2 transition-colors rounded-lg hover:bg-white/10 ${
              isFavorited ? 'text-gold-400' : 'text-white/60 hover:text-gold-400'
            }`}
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart size={18} className={isFavorited ? 'fill-current' : ''} />
          </button>
          <button
            onClick={handleNoteClick}
            className={`p-2 transition-colors rounded-lg hover:bg-white/10 ${
              hasNotes ? 'text-gold-400' : 'text-white/60 hover:text-gold-400'
            }`}
            title={hasNotes ? 'Edit note' : 'Add note'}
          >
            <MessageSquare size={18} className={hasNotes ? 'fill-current' : ''} />
          </button>
        </div>
      </div>

      <p className="text-white/80 font-serif text-lg leading-relaxed">
        {verse.text}
      </p>

      {showContext && (
        <button
          onClick={handleReadInContext}
          className="mt-4 flex items-center gap-2 text-sm text-white/40 hover:text-gold-400 transition-colors group"
        >
          <BookOpen size={16} />
          <span>Read in context</span>
          <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      )}

      {isNoteModalOpen && (
        <NoteModal
          isOpen={isNoteModalOpen}
          onClose={() => setIsNoteModalOpen(false)}
          verse={verse}
          onNotesChange={(hasNotes) => setHasNotes(hasNotes)}
        />
      )}
    </div>
  );
}